<template>
  <div class="ticket-selection">
    <div
      class="ticket-selection__bookables"
      :class="[isUpdating && 'opacity-60 pointer-events-none']"
    >
      <h4 class="ticket-selection__bookables-title">
        {{ $t('landingpage.checkout-page.ticket-quantity-picker.title') }}
      </h4>

      <div class="ticket-selection__bookables-intro">
        <p>
          {{ $t('landingpage.checkout-page.ticket-quantity-picker.intro') }}
        </p>
      </div>

      <div v-if="isFetching">
        <FontAwesomeIcon
          :icon="['fal', 'spinner']"
          class="mx-auto mb-3 block text-2xl"
          spin
        />
      </div>

      <div v-else class="ticket-selection__bookables-list">
        <label
          v-for="ticket in tickets"
          :key="ticket.id"
          class="ticket-selection__bookable-item"
          :class="[
            quantities[ticket.id] > 0 &&
              'ticket-selection__bookable-item--active',
            isComingSoon(ticket) &&
              'ticket-selection__bookable-item--coming-soon',
          ]"
        >
          <div class="ticket-selection__bookable-content">
            <div class="ticket-selection__bookable-details">
              <p class="ticket-selection__bookable-name">
                {{ ticket.name[activeLocale] }}

                <span
                  v-if="ticket?.price === 0 && !isComingSoon(ticket)"
                  class="ticket-selection__bookable-free"
                >
                  {{ $t('landingpage.checkout-page.ticket-selection.free') }}
                </span>
                <span
                  v-if="isComingSoon(ticket)"
                  class="ticket-selection__bookable-coming-soon"
                >
                  {{
                    $t('landingpage.checkout-page.ticket-selection.coming-soon')
                  }}
                </span>
              </p>

              <p
                class="ticket-selection__bookable-description"
                v-text="ticket.description?.[activeLocale]"
              />

              <p
                v-if="isComingSoon(ticket)"
                class="ticket-selection__bookable-availability"
              >
                <template v-if="ticket.sales_start">
                  {{
                    $t(
                      'landingpage.checkout-page.ticket-selection.available-from',
                      { date: formatSalesStart(ticket.sales_start) },
                    )
                  }}
                </template>
              </p>
              <p
                v-else
                class="ticket-selection__bookable-availability"
                :class="
                  (ticket.remaining ?? 0) === 0 &&
                  'ticket-selection__bookable-availability--none'
                "
              >
                {{
                  $t('landingpage.checkout-page.ticket-selection.available', {
                    count: ticket.remaining ?? 0,
                  })
                }}
              </p>

              <div
                v-if="!isComingSoon(ticket)"
                class="ticket-selection__bookable-selector-container"
              >
                <QuantitySelector
                  class="ticket-selection__bookable-selector"
                  :max="ticket.remaining ?? 0"
                  :value="quantities[ticket.id] ?? 0"
                  @change="(value) => onQuantityChange(ticket.id, value)"
                />
              </div>
            </div>

            <div
              v-if="ticket?.price > 0"
              class="ticket-selection__bookable-price"
            >
              <p class="ticket-selection__bookable-price-label">
                {{
                  $t(
                    'landingpage.checkout-page.ticket-selection.bookables.price',
                  )
                }}
              </p>
              <p class="ticket-selection__bookable-price-value">
                {{
                  formatCurrencyLeft(
                    ticket?.price,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    orderDetails?.currency,
                  )
                }}
              </p>
            </div>
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, watch } from 'vue'
import QuantitySelector from '@/Components/common/QuantitySelector.vue'
import { formatCurrencyLeft } from '@/composables/currency'
import {
  formatTicketSalesStart,
  isTicketComingSoon as isComingSoon,
} from '@/LandingPage/Checkout/ticketSalesStatus'

const props = defineProps({
  tickets: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: () => false,
  },
  isUpdating: {
    type: Boolean,
    default: () => false,
  },
})

const emit = defineEmits(['tickets-selected'])

const orderDetails = inject('orderDetails')
const activeLocale = inject('activeLocale')
const eventSettings = inject('eventSettings', null)

const formatSalesStart = (salesStart) =>
  formatTicketSalesStart(salesStart, eventSettings?.timezone, activeLocale)

const quantities = ref({})

watch(
  () => props.tickets,
  (newTickets) => {
    newTickets.forEach((ticket) => {
      if (quantities.value[ticket.id] === undefined) {
        quantities.value[ticket.id] = 0
      }
    })
  },
  { immediate: true },
)

// Initialize quantities from existing line items
watch(
  () => orderDetails.value?.line_items,
  (lineItems) => {
    if (!lineItems?.length) return
    lineItems.forEach((lineItem) => {
      if (
        lineItem.product_type?.includes('TicketBookable') &&
        lineItem.bookable_id
      ) {
        quantities.value[lineItem.bookable_id] = lineItem.quantity
      }
    })
  },
  { immediate: true },
)

const onQuantityChange = (ticketId, value) => {
  quantities.value[ticketId] = value

  const ticketSelections = props.tickets
    .filter((ticket) => (quantities.value[ticket.id] ?? 0) > 0)
    .map((ticket) => ({
      ticket_id: ticket.id,
      quantity: quantities.value[ticket.id],
    }))

  emit('tickets-selected', ticketSelections)
}
</script>
